import TextButtonIcon from "@@/components/Button/TextButtonIcon";
import Box from "@@/elements/Box";
import { ArrowRight } from "@@/elements/Icons/icons";
import Picture from "@@/elements/Picture";
import Typography from "@@/elements/Typography";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import { PictureData } from "@@/models/pictureData";
import cn from "classnames";
import { CardColors } from "../CardProps";
import cardStyles from "../cardStyles.module.scss";
import styles from "./index.module.scss";

export type InfoCardVariant = "image" | "color";

export type InfoCardProperties = {
  header: string;
  body: string;
  image?: PictureData;
};

export type InfoCardOwnProps = {
  variant: InfoCardVariant;
  color?: CardColors;
  link?: LinkData;
  fixedWidth?: boolean;
  anchorId?: string;
};

export type InfoCardProps = InfoCardOwnProps & InfoCardProperties;

export const InfoCard = ({
  header,
  variant,
  color,
  body,
  link,
  image,
  fixedWidth,
  anchorId,
}: InfoCardProps) => {
  const cardClassNames = cn(cardStyles.card, {
    [(cardStyles as any)[`card_${color}`]]: color && color != "beige",
    [(cardStyles as any).card_beigeTint]: color && color === "beige",
  });

  return (
    <Box
      id={anchorId}
      height="full"
      width={fixedWidth ? 80 : "full"}
      justifyContent="center">
      {variant === "image" && (
        <Box
          width="full"
          height="full"
          flexDirection="column">
          <EpiProperty<InfoCardProperties> name="image">
            <Picture
              className={styles.info_card__image}
              imageData={image}
              aspectRatio={"4 / 3"}
              sizes={[200, 400, 600, 800, 1200]}
            />
          </EpiProperty>
          <Box
            flexDirection="column"
            className={styles.text_content}
            textAlign="left"
            gap={6}
          >
            <Box>
              <EpiProperty<InfoCardProperties> name="header">
                <Typography
                  variant="heading5"
                  asElement="h3"
                  mb={3}
                >
                  {header}
                </Typography>
              </EpiProperty>
              <EpiProperty<InfoCardProperties> name="body">
                <Typography variant="information">{body}</Typography>
              </EpiProperty>
            </Box>
            {link && link.text && (
              <Box>
                <TextButtonIcon
                  link={link}
                  label={link.text}
                  dense
                  direction="right"
                >
                  <ArrowRight />
                </TextButtonIcon>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {variant === "color" && (
        <Box
          className={cn(cardClassNames)}
          width="full"
          px={[6, 10, 10]}
          py={[8, 10, 10]}
        >
          <Box
            flexDirection="column"
            className={styles.text_content_color}
            textAlign="left"
            gap={8}
            width="full"
          >
            <Box>
              <EpiProperty<InfoCardProperties> name="header">
                <Typography
                  variant="heading5"
                  asElement="h3"
                  textcolor={color === "beige" ? "dark" : "light"}
                  mb={3}
                >
                  {header}
                </Typography>
              </EpiProperty>
              <EpiProperty<InfoCardProperties> name="body">
                <Typography
                  textcolor={color === "beige" ? "dark" : "light"}
                  variant="information">{body}</Typography>
              </EpiProperty>
            </Box>
            {link && link.text && (
              <Box>
                <TextButtonIcon
                  color={color === "beige" ? "charcoal" : "white"}
                  link={link}
                  label={link.text}
                  dense
                  direction="right"
                >
                  <ArrowRight />
                </TextButtonIcon>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export type ProvidedInfoCardProps = OptimizelyBlockProps<InfoCardProperties> &
InfoCardProps;

const ProvidedInfoCard = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedInfoCardProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => <InfoCard
      anchorId={anchorId}
      {...rest}
      {...optimizlyProperties} />}
  </OptimizelyBlock>
);

export default ProvidedInfoCard;
